import {
  Col,
  Form,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
} from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import './ReassignApprovalForm.scss';
import Icon from '@mdi/react';
import { mdiAccountOutline } from '@mdi/js';
import {
  initialValues,
  reassignTypeOptions,
  validationSchema,
} from './constants';
import { UserAsyncAutocompleteSelect } from '../../Common';
import { updateReassignApproval } from '../../../store/apis/reassignApprovalService';
import { fetchDelegateApprovalAutoComplete } from '../../../store/apis/autocompleteService';

function ReassignApprovalForm({ isOpen, toggle, selectedData, closeModal }) {
  const queryClient = useQueryClient();
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const updateReassignApprovalMutation = useMutation({
    mutationFn: payloadList => updateReassignApproval(payloadList),
  });

  const onSubmit = async values => {
    // Handle form submission
    const payloadList = selectedData.map(item => ({
      approvalType: item.ApprovalTyp,
      approvalRank: item?.ApprovalRank,
      requestId: item?.RequestId,
      reassignType: values.reassignType,
      newApprover: +values.reassignTo.value,
      reason: values.reason,
      coCd: item?.CoCd?.trim(),
    }));
    await updateReassignApprovalMutation.mutateAsync(payloadList);
    toast.success('Reassignment Successful!', {
      className: 'toast-success',
    });
    queryClient.invalidateQueries(['ReassignApprovals']);
    closeModal();
  };

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      className="reassign-oc"
    >
      <OffcanvasHeader className="reassign-header">
        Reassign Approval
      </OffcanvasHeader>
      <Form onSubmit={handleSubmit(onSubmit)} className="reassign-form">
        <OffcanvasBody className="reassign-body">
          <Row className="gy-3">
            <Col md={6}>
              <div className="d-flex flex-column gap-1">
                <Label>
                  Reassign Type<span className="text-danger">*</span>
                </Label>
                <Controller
                  name="reassignType"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="select"
                      className={`form-select external-filter-input ${errors.reassignType ? 'is-invalid' : ''}`}
                    >
                      <option value="">Select Reassign Type</option>
                      {reassignTypeOptions?.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </Input>
                  )}
                />
                {errors.reassignType && (
                  <span className="text-danger">
                    {errors.reassignType.message}
                  </span>
                )}
              </div>
            </Col>
            <Col md={6}>
              {/* Login Name */}
              <div className="d-flex flex-column gap-1">
                <Label>
                  Reassign To<span className="text-danger">*</span>
                </Label>
                <Controller
                  name="reassignTo"
                  control={control}
                  render={({ field }) => (
                    <UserAsyncAutocompleteSelect
                      autoCompleteFn={input =>
                        fetchDelegateApprovalAutoComplete({
                          prefixText: input,
                        })
                      }
                      mappedFn={rawData =>
                        rawData.map(item => ({
                          value: item.ID,
                          label: item.Name,
                          name: item.Name,
                          code: item.Cd,
                          designation: item.Designation,
                          image: item?.Imagefile,
                          coCd: item?.CoCd?.trim(),
                        }))
                      }
                      onChange={option =>
                        setValue('reassignTo', option, { shouldValidate: true })
                      }
                      placeholder="Reassign To"
                      isClearable
                      isSearchable
                      value={field?.value}
                      inputIcon={
                        <Icon
                          path={mdiAccountOutline}
                          size={0.8}
                          color="#667085"
                        />
                      }
                      rightKeyAssessor="code"
                    />
                  )}
                />
                {errors.reassignTo && (
                  <span className="text-danger">Reassign To is required</span>
                )}
              </div>
            </Col>
            <Col md={12}>
              <div className="d-flex flex-column gap-1">
                <Label>
                  Reason<span className="text-danger">*</span>
                </Label>
                <Controller
                  name="reason"
                  control={control}
                  render={({ field }) => (
                    <Input
                      placeholder="Enter Reason"
                      type="textarea"
                      rows="5"
                      {...field}
                      className="form-control external-filter-input"
                    />
                  )}
                />
                {errors.reason && (
                  <span className="text-danger">{errors.reason.message}</span>
                )}
              </div>
            </Col>
          </Row>
        </OffcanvasBody>
        <div className="reassign-footer d-flex flex-row align-items-center gap-2 p-2">
          <Col>
            <button
              type="button"
              className="w-100 custom-light-btn"
              onClick={() => {
                reset();
                toggle();
              }}
            >
              Cancel
            </button>
          </Col>
          <Col>
            <button
              type="submit"
              disabled={!isValid}
              className="w-100 custom-btn"
              onClick={toggle}
            >
              Update
            </button>
          </Col>
        </div>
      </Form>
    </Offcanvas>
  );
}

export default ReassignApprovalForm;
